const TEMPLATE_VARIABLE_RE = /{{\s*([a-z0-9_]+)\s*}}/gi;

export const createEmptyRecruitmentContact = () => ({
  localId: `${Date.now()}-${Math.random()}`,
  first_name: "",
  last_name: "",
  email_primary: "",
  position: "",
  phone_primary: "",
});

export const normalizeRequirementType = (value) =>
  String(value || "internal").trim().toLowerCase() === "client" ? "client" : "internal";

export const renderRecruitmentTemplatePreview = (template, variables = {}) =>
  String(template || "").replace(TEMPLATE_VARIABLE_RE, (_match, key) => String(variables[key] ?? ""));

export const submissionProgressLabel = (application = {}) => {
  const sent = Number(application.submission_sent_count || 0);
  const recipients = Number(application.submission_recipient_count || 0);
  return `${sent}/${recipients}`;
};

export const submissionStatusPresentation = (application = {}) => {
  const status = String(application.submission_status || "not_sent");
  const base = { status, progress: submissionProgressLabel(application) };
  if (status === "sent") return { ...base, icon: "mdi-email-check-outline", color: "success" };
  if (status === "partial") return { ...base, icon: "mdi-email-fast-outline", color: "warning" };
  if (status === "error") return { ...base, icon: "mdi-email-alert-outline", color: "error" };
  if (status === "unknown") return { ...base, icon: "mdi-help-circle-outline", color: "warning" };
  return { ...base, icon: "mdi-email-arrow-right-outline", color: "primary" };
};

export const createSubmissionIdempotencyKey = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `recruitment:${crypto.randomUUID()}`;
  }
  return `recruitment:${Date.now()}:${Math.random().toString(36).slice(2, 14)}`;
};

export const readStoredRecruitmentRoles = (storage = globalThis?.localStorage) => {
  const roles = new Set();
  const add = (value) => {
    const normalized = String(value || "").trim().toLowerCase();
    if (normalized) roles.add(normalized);
  };
  try {
    const entries = JSON.parse(storage?.getItem("user-roles") || "[]");
    if (Array.isArray(entries)) {
      entries.forEach((entry) => add(
        typeof entry === "string" ? entry : entry?.role_key || entry?.key || entry?.role || entry?.name
      ));
    }
  } catch {
    // Legacy role remains a safe fallback when stored JSON is malformed.
  }
  add(storage?.getItem("user-primary-role"));
  add(storage?.getItem("user-role"));
  return roles;
};

export const hasRecruitmentContactDuplicate = (existingPeople = [], newContacts = []) => {
  const seenEmails = new Set();
  const seenNames = new Set();
  const existingEmails = new Set(
    existingPeople.map((person) => String(person.email_primary || "").trim().toLowerCase()).filter(Boolean)
  );
  const existingNames = new Set(
    existingPeople.map((person) =>
      [person.first_name, person.last_name].filter(Boolean).join(" ").trim().toLowerCase()
    ).filter(Boolean)
  );
  return newContacts.some((contact) => {
    const email = String(contact.email_primary || "").trim().toLowerCase();
    const name = [contact.first_name, contact.last_name].filter(Boolean).join(" ").trim().toLowerCase();
    const duplicate = (email && (seenEmails.has(email) || existingEmails.has(email)))
      || (name && (seenNames.has(name) || existingNames.has(name)));
    if (email) seenEmails.add(email);
    if (name) seenNames.add(name);
    return duplicate;
  });
};

export const buildRecruitmentRequirementPayload = ({
  form,
  companies,
  creatingCompany,
  newCompanyName,
  selectedContactIds,
  newContacts,
}) => {
  const tenant = companies.find((company) => Number(company.id) === Number(form.id_companies));
  const payload = {
    id_companies: form.id_companies,
    name_companies: tenant?.company_name || null,
    name_vacancy: String(form.name_vacancy || "").trim(),
    requirement_type: form.requirement_type,
  };
  if (form.requirement_type !== "client") return payload;
  if (creatingCompany) payload.new_client_company = { name: String(newCompanyName || "").trim() };
  else payload.ncrm_client_company_id = form.ncrm_client_company_id || null;
  payload.contact_person_ids = selectedContactIds;
  payload.new_contacts = newContacts.map((contact) => ({
    first_name: contact.first_name,
    last_name: contact.last_name,
    email_primary: contact.email_primary,
    position: contact.position || null,
    phone_primary: contact.phone_primary || null,
  }));
  return payload;
};
